<template>
    <div>
        <div class="text-right">
            <Button v-if="!disableMaterialSaveAction" class="el-button el-button--secondary"
                @click="confirmDeleteSelectedMaterials">
                <i circle>
                    <svgicon name="icon-delete-md" width="14" height="14" />
                </i></Button>
            <Button class="el-button el-button--secondary"
                :style="disableMaterialSaveAction ? 'cursor: not-allowed; opacity: 0.6;' : ''"
 :disabled="disableMaterialSaveAction || hasEmptyFields" @click="save">Save</Button>
        </div>
        <div class="my-15 table-with-add" style="width: 92%;">
            <div class="add-block" @click="addNewMaterialExcess" style="margin-top: 1.1rem;">
                {{ $t('ADD_MATERIAL') }}
            </div>
            <div>
                <Table max-height="260" class="table-striped bordered mt-10" :data="mergedMaterials"
                    highlight-current-row :row-key="row => row.id ? `id-${row.id}` : `sno-${row.sno}`" lazy
                    aria-describedby="excessmaterialDetailsTableData">
                    <TableColumn label=" " width="40">
                        <template slot-scope="header">
                            <Checkbox :value="areAllRowsSelected" @change="toggleSelectAll" />
                        </template>

                        <template slot-scope="scope">
                            <Checkbox :checked="isRowSelected(scope.row)" @change="toggleRowSelection(scope.row)"
                                class="pointer" name="material-excess" />
                        </template>
                    </TableColumn>

                    <TableColumn v-for="(item, index) in getExcessMaterialsHeaders" :key="index" :label="item.title"
                        :width="item.width">
                        <template slot-scope="scope">
                            <span class="d-block">
                                <Input v-if="item.type === 'input'" :placeholder="getInputPlaceholder(item, scope.row)"
                                    class="input-blank p-0 bold" size="mini" :required="item.required" type="text"
                                    :value="getInputModel(item, scope.row)" :disabled="item.disabled"
                                    @input="val => setInputModel(item, scope.row, val)"
                                    :readonly="disableMaterialSaveAction" />
                                <div>
                                    <Attachments v-if="item.type === 'attachment'"
                                        :attachments="getFilteredAttachments(scope.row)" :multiple="true"
                                        @addAttachments="(file) => addAttachments(file, scope.row, scope.$index)"
                                        @removeAttachments="(file) => removeAttachments(file, scope.row, scope.$index)"
                                        :readOnly="disableMaterialSaveAction" :smallIcons="true" :showPreview="true">
                                    </Attachments>
                                </div>
                            </span>
                        </template>
                    </TableColumn>
                </Table>
            </div>
        </div>
    </div>
</template>

<script>
import { materialExcessColumns } from '@/constants/material-excess';
import Attachments from './Attachments';


export default {
    props: {
        selectedConsignee: {
            type: Object,
            required: true
        },
        excessMaterials: {
            type: Array,
            default: function () {
                return [];
            }
        },
        getMaterialExcess: {
            type: Function,
            default: function () { }
        },
        materialHeaders: {
            type: Object,
            default: function () {
                return {};
            }
        },
        orderDetails: {
            type: Object,
            default: function () {
                return {};
            }
        },
        isShipper: {
            type: Boolean,
            default: false
        },

    },
    components: {
        Attachments,
    },
    data() {
        // Deep clone excessMaterials to avoid mutating Vuex state
        function deepClone(obj) {
            return JSON.parse(JSON.stringify(obj));
        }
        const initializedMaterials = deepClone(this.excessMaterials || []).map(function (item) {
            return Object.assign({}, item, {
                custom_fields: item.custom_fields || {},
                isUpdated: false
            });
        });
        return {
            materialExcessColumns,
            localMaterials: [],
            attachments: {},
            modifiedExcessMaterials: initializedMaterials,
            selectedRows: [], // holds rows selected for multi-delete
        };
    },
    watch: {
        excessMaterials: {
            handler(newVal) {
                function deepClone(obj) {
                    return JSON.parse(JSON.stringify(obj));
                }
                const initializedMaterials = deepClone(newVal || []).map(function (item) {
                    return Object.assign({}, item, {
                        custom_fields: item.custom_fields || {},
                        isUpdated: false
                    });
                });
                this.modifiedExcessMaterials = initializedMaterials;
            },
            deep: true
        },
    },
    computed: {
        areAllRowsSelected() {
            return this.selectedRows.length === this.mergedMaterials.length && this.selectedRows.length > 0;
        },
        isIndeterminate() {
            return this.selectedRows.length > 0 && this.selectedRows.length < this.mergedMaterials.length;
        },
        allSelected() {
            return (
                this.mergedMaterials.length > 0 &&
                this.selectedRows.length === this.mergedMaterials.length
            );
        },
        hasEmptyFields() {
            return this.hasInvalidRows();
        },
        isInvalidQuantityRow(row) {
            const isRowTouched = Object.keys(row).some(key => {
                // Ignore 'id' or keys that are always default
                return key !== 'id' && row[key] !== '' && row[key] !== null && row[key] !== undefined;
            });

            const isRefInvalid = !row.material_ref_id || row.material_ref_id.length <= 0;
            const isQuantityInvalid = !row.quantity || isNaN(row.quantity) || Number(row.quantity) <= 0;

            return isRowTouched && isQuantityInvalid && isRefInvalid;
        },
        getExcessMaterialsHeaders() {
            let dbHeaders = [];
            if (Array.isArray(this.materialHeaders.excess_materials_custom_fields)) {
                dbHeaders = this.materialHeaders.excess_materials_custom_fields;
            } else if (Array.isArray(this.orderDetails.excess_materials_custom_fields)) {
                dbHeaders = this.orderDetails.excess_materials_custom_fields;
            }

            // Build custom headers
            const customHeaders = dbHeaders.map(item => ({
                title: item.label || item.title,
                paramKey: item.name,
                type: item.type === 'textbox' ? 'input' : item.type || 'input',
                required: item.required || false,
                width: item.width || '150px',
                disabled: item.disabled || false,
                isCustom: true
            }));

            // Add isCustom to parent headers
            const parentHeaders = this.materialExcessColumns.map(col => {
                const header = Object.assign({}, col, { isCustom: false });
                // Change header title if feature flag is enabled
                if (header.paramKey === 'material_ref_id' && this.$hasFeature('add_excess_material_overiding_rejection')) {
                    header.title = 'Material Code';
                }
                return header;
            });

            // Merge all headers first
            const allHeaders = [...parentHeaders, ...customHeaders];

            // Sort: non-attachments first, then attachments
            const nonAttachments = allHeaders.filter(h => h.type !== 'attachment');
            const attachments = allHeaders.filter(h => h.type === 'attachment');

            return [...nonAttachments, ...attachments];
        },
        checkDeletePermission() {
            return this.$hasPermission('save_material_excess');
        },
        disableMaterialSaveAction() {
            const orderStatus = this.orderDetails.status;
            const dispatchedStatus = this.$statusUtil.isDispatched(orderStatus);
            const afterDispatched = this.$statusUtil.isAfterDispatched(orderStatus);
            const delivered = this.$statusUtil.isDelivered(orderStatus);

            let isEditable;

            if (this.isShipper) {
                // Shipper: allow only after dispatched (strictly after), not during dispatched or delivered
                isEditable = afterDispatched && !delivered;
            } else {
                // Transporter: allow during dispatched and after dispatched, not delivered
                isEditable = (dispatchedStatus || afterDispatched) && !delivered;
            }

            const hasPermission = this.$hasPermission('save_material_excess');

            const shouldDisable = delivered || !hasPermission || !isEditable;

            return shouldDisable;
        },
        approvalMaterialExcessColumns() {
            return this.materialExcessColumns;
        },

        filteredExcessMaterials() {
            if (!this.selectedConsignee || !this.selectedConsignee.location_id) {
                return this.modifiedExcessMaterials;
            }
            return this.modifiedExcessMaterials.filter(material =>
                material.destination_location_id === this.selectedConsignee.location_id
            );
        },

        filteredLocalMaterials() {
            if (!this.selectedConsignee || !this.selectedConsignee.location_id) {
                return this.localMaterials.filter(item => item !== null && item !== undefined);
            }
            return this.localMaterials.filter(item =>
                item !== null &&
                item !== undefined &&
                item.destination_location_id === this.selectedConsignee.location_id
            );
        },

        mergedMaterials() {
            // If no consignee is selected, show nothing (or all, as you prefer)
            if (!this.selectedConsignee || !this.selectedConsignee.location_id) {
                return [];
            }
            // Filter both arrays by destination_location_id
            const locationId = this.selectedConsignee.location_id;
            return [
                ...this.modifiedExcessMaterials.filter(row => row.destination_location_id === locationId),
                ...this.localMaterials.filter(row => row.destination_location_id === locationId)
            ];
        }

    },
    methods: {
        toggleSelectAll(event) {
            const isChecked = event.target.checked;

            if (isChecked) {
                // Select all rows
                this.selectedRows = [...this.mergedMaterials];
            } else {
                // Clear all selections
                this.selectedRows = [];
            }
        },
        toggleRowSelection: function (row) {
            var self = this;
            var index = this.selectedRows.findIndex(function (selected) {
                return (row.id && selected.id === row.id) ||
                    (row.sno && selected.sno === row.sno);
            });

            if (index >= 0) {
                // Remove from selection
                this.selectedRows.splice(index, 1);
            } else {
                // Add to selection - ES5 compatible object clone
                var newSelection = {};
                for (var key in row) {
                    if (row.hasOwnProperty(key)) {
                        newSelection[key] = row[key];
                    }
                }
                // Add unique key for tracking
                newSelection._selectionKey = row.id ? 'id-' + row.id : 'sno-' + row.sno;
                this.selectedRows.push(newSelection);
            }
        },
        isRowSelected(row) {
            return this.selectedRows.some(selected =>
                (row.id && selected.id === row.id) ||
                (row.sno && selected.sno === row.sno)
            );
        },
        getRowKey(row, index) {
            return row.id || `temp-${index}`;
        },
        async confirmDeleteSelectedMaterials() {
            if (this.selectedRows.length === 0) return;

            const result = await this.$PromptConfirm(
                `Are you sure you want to delete ${this.selectedRows.length} selected excess materials?`,
                'Delete Selected Excess Materials',
                {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                }
            );

            if (result === 'confirm') {
                await this.deleteMultipleRows(this.selectedRows);
            }
        },

        async deleteMultipleRows(rows) {
            if (!rows || rows.length === 0) return;

            const rowsWithId = rows.filter(row => row.id);

            try {
                if (rowsWithId.length > 0) {
                    const idsToDelete = rowsWithId.map(row => row.id);
                    await this.$store.dispatch('order/bulkDeleteExcessMaterials', idsToDelete);
                }

                rows.forEach(row => {
                    this.removeFromAllStates(row);
                });

                this.selectedRows = [];

                this.$successMessage({
                    message: `${rows.length} excess material${rows.length > 1 ? 's' : ''} deleted successfully.`,
                });


            } catch (error) {
                this.$errorMessage({
                    message: 'Error deleting selected excess materials: ' + error.message,
                });
            }
        },
        hasInvalidRows() {
            return this.mergedMaterials.some(row => {
                return !row.quantity || !row.material_ref_id;
            });
        },
        markRowUpdated(row) {
            if (!row) {
                console.warn('No row provided to mark as updated');
                return;
            }

            // Directly set the flag on the row object
            this.$set(row, 'isUpdated', true);

        },
        removeFromLocalState(info) {
            this.localMaterials = this.localMaterials.filter(data =>
                !(data.id === info.id && data.sno === info.sno)
            );

            // Force Vue to recognize the array change
            this.$forceUpdate();
        },
        addNewMaterialExcess() {
            if (this.disableMaterialSaveAction) {
                return;
            }
            const newRow = {
                material_ref_id: '',
                quantity: '',
                quantity_uom: '',
                delivery_no: '',
                material_invoice_no: '',
                line_item_no: '',
                remarks: '',
                custom_fields: {},
                attachment_ids: [],
                sno: Date.now(),
                isUpdated: true,
                destination_location_id: this.selectedConsignee.location_id,
                destination_gate_id: this.selectedConsignee.gate_id,
                source_location_id: this.selectedConsignee.source_location_id,
                source_gate_id: this.selectedConsignee.source_gate_id,
            };

            // Add all custom fields as empty strings
            const columns = this.getExcessMaterialsHeaders || [];
            columns.forEach(column => {
                if (column.type === 'input' && column.paramKey) {
                    newRow.custom_fields[column.paramKey] = '';
                }
                // If you have dropdowns, handle them here as well
                if (column.type === 'dropdown' && column.paramKey) {
                    newRow.custom_fields[column.paramKey] = [];
                }
            });

            this.localMaterials.push(newRow);
        },

        async confirmDeleteMaterial(material) {
            var self = this;
            const result = await this.$PromptConfirm('Are you sure want to delete the selected excess material ?', 'Delete Excess Material', {
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
            });
            if (result === 'confirm') {
                await this.deleteRows(material);
            }
        },

        // In deleteRows() or deleteMultipleRows()
        async deleteRows(info) {
            try {
                // Clear selection first
                this.selectedRows = this.selectedRows.filter(
                    selected => !((selected.id === info.id) || (selected.sno === info.sno))
                );

                // Proceed with deletion
                this.removeFromAllStates(info);

                if (info.id) {
                    await this.$store.dispatch('order/deleteExcessMaterial', info.id);
                }

                // Force update to refresh table
                this.$nextTick(() => {
                    this.$forceUpdate();
                });
            } catch (error) {
                console.error('Delete error:', error);
                this.$errorMessage({ message: 'Failed to delete material' });
            }
        },

        // Unified removal from all data sources
        // Improved removeFromAllStates
        removeFromAllStates(info) {
            // Clear selection
            this.selectedRows = this.selectedRows.filter(
                selected => !((selected.id === info.id) || (selected.sno === info.sno))
            );

            // Remove from data
            this.modifiedExcessMaterials = this.modifiedExcessMaterials.filter(
                item => !this.isSameItem(item, info)
            );
            this.localMaterials = this.localMaterials.filter(
                item => !this.isSameItem(item, info)
            );
        },

        // Helper to compare items
        isSameItem(a, b) {
            return (a.id && b.id && a.id === b.id) ||
                (a.sno && b.sno && a.sno === b.sno);
        },

        // Recovery method if API fails
        reAddDeletedRow(info) {
            if (info.isLocal) {
                this.localMaterials = [...this.localMaterials, info];
            } else {
                this.modifiedExcessMaterials = [...this.modifiedExcessMaterials, info];
            }
        },

        loadCustomFieldData(value) {

            return this.materialHeaders.excess_materials_custom_fields.map((_customField) => {
                return { [_customField.name]: value[_customField.name] || '' };

            });
        },

        async save() {
            try {
                // 1. Filter only modified existing rows and all new local rows
                const modifiedExistingRows = Array.isArray(this.modifiedExcessMaterials)
                    ? this.modifiedExcessMaterials.filter(row => row.isUpdated)
                    : [];
                const allChangedRows = [...modifiedExistingRows, ...this.localMaterials];

                if (allChangedRows.length === 0) {
                    this.$message.info('No changes to save');
                    return;
                }

                // 2. Get custom field keys from materialHeaders
                const customFieldKeys = Array.isArray(this.materialHeaders.excess_materials_custom_fields)
                    ? this.materialHeaders.excess_materials_custom_fields.map(f => f.name)
                    : Array.isArray(this.orderDetails.excess_materials_custom_fields) ? this.orderDetails.excess_materials_custom_fields.map(f => f.name) : [];

                // 3. Format materials for API request with only required fields
                const formattedMaterials = allChangedRows.map(data => {
                    // For new rows (no id), use selectedConsignee for source/destination fields
                    const destination_location_id = data.destination_location_id !== undefined && data.destination_location_id !== null
                        ? data.destination_location_id
                        : (this.selectedConsignee && this.selectedConsignee.location_id) || null;

                    const destination_gate_id = data.destination_gate_id !== undefined && data.destination_gate_id !== null
                        ? data.destination_gate_id
                        : (this.selectedConsignee && this.selectedConsignee.gate_id) || null;

                    const source_location_id = data.source_location_id !== undefined && data.source_location_id !== null
                        ? data.source_location_id
                        : (this.selectedConsignee && this.selectedConsignee.source_location_id) || null;

                    const source_gate_id = data.source_gate_id !== undefined && data.source_gate_id !== null
                        ? data.source_gate_id
                        : (this.selectedConsignee && this.selectedConsignee.source_gate_id) || null;

                    // Build custom_fields object with only the allowed keys
                    let customFieldsObj = {};
                    customFieldKeys.forEach(key => {
                        if (data.custom_fields && data.custom_fields[key] !== undefined) {
                            customFieldsObj[key] = data.custom_fields[key];
                        } else if (data[key] !== undefined) {
                            customFieldsObj[key] = data[key];
                        } else {
                            customFieldsObj[key] = '';
                        }
                    });

                    return {
                        id: data.id, // <-- include this if present!
                        order_no: this.$route.params.orderId || this.$route.params.orderNo, // or data.order_no if available
                        material_ref_id: data.material_ref_id || '',
                        quantity: data.quantity ? Number(data.quantity) : null,
                        quantity_uom: data.quantity_uom || '',
                        delivery_no: data.delivery_no || '',
                        material_invoice_no: data.material_invoice_no || '',
                        line_item_no: data.line_item_no || '',
                        remarks: data.remarks || '',
                        source_location_id: source_location_id || null,
                        source_gate_id: source_gate_id || null,
                        destination_location_id: destination_location_id || null,
                        destination_gate_id: destination_gate_id || null,
                        attachment_ids: data.attachment_ids,
                        custom_fields: customFieldsObj
                    };
                });

                // 4. Make API call to save materials
                const result = await this.$store.dispatch('order/addExcessMaterials', formattedMaterials);
                if (result && result.error || result.status != 201) {
                    let errorMessage = this.$getSuccess('EXCESS_MATERIAL_FAILED', {
                        firstArgument: result.data && result.data.errors && result.data.errors[0].message || result.error.message
                    });
                    this.$errorMessage({ message: errorMessage });
                } else {
                    let successMessage = result && result.isUpdateOperation;
                    this.$successMessage({
                        message: successMessage ? this.$getSuccess('EXCESS_MATERIAL_UPDATED') : this.$getSuccess('EXCESS_MATERIAL_SUCCESS')
                    });
                    // 1. Remove all local rows that were just saved (those without an id)
                    //    or those that match the new server data (by a unique field)
                    //    Here, we remove all local rows (since they are now in server data)
                    this.localMaterials = [];

                    // 2. Replace modifiedExcessMaterials with the latest server data
                    //    (or merge, but avoid duplicates)
                    if (Array.isArray(result.data)) {
                        this.modifiedExcessMaterials = result.data.map(item =>
                            Object.assign({}, item, {
                                custom_fields: item.custom_fields || {},
                                isUpdated: false
                            })
                        );
                    }

                    // Log the updated state

                    // Emit event to parent
                    this.$emit('refetch-excess-materials');

                    // Force UI update
                    this.$nextTick(() => this.$forceUpdate());
                }
            } catch (error) {
                console.error('Save error:', error);
                this.$errorMessage({
                    message: this.$getErr('EXCESS_MATERIAL_FAILED', {
                        firstArgument: error.message,
                    })
                });
                this.$nextTick(() => this.$forceUpdate());
            }
        },

        getInputPlaceholder(item, row) {
            if (item.isCustom) {
                return row.custom_fields[item.paramKey] || 'Enter ' + item.title;
            } else {
                return row[item.paramKey] || 'Enter ' + item.title;
            }
        },

        getInputModel(item, row) {
            return item.isCustom ? row.custom_fields[item.paramKey] : row[item.paramKey];
        },

        setInputModel(item, row, value) {
            if (item.isCustom) {
                this.$set(row.custom_fields, item.paramKey, value);
            } else {
                this.$set(row, item.paramKey, value);
            }
            row.isUpdated = true;
        },

        getFilteredAttachments(row) {
            if (!row.attachment_ids || !row.attachment_ids.length) {
                return [];
            }
            return row.attachment_ids.map((att) => {
                return {
                    uid: att.id,
                    id: att.id,
                    name: att.name,
                    url: att.attachment_url || att.url,
                };
            });
        },

        async addAttachments(file, row, index) {
            try {
                this.markRowUpdated(row);
                // 1. Prepare FormData
                const formData = new FormData();
                formData.append('attachment', file.raw);
                // 2. Make API call
                const result = await this.$store.dispatch('order/addAttachment', formData);

                // 3. Check for errors
                if (result.error) {
                    console.error('Attachment upload failed:', result.error);
                    this.$error({ message: result.error || this.$getErr('UPLOAD_FAILS') });
                    return;
                }

                // 4. Validate response
                if (!result.id || !result.attachment_url) {
                    throw new Error('Invalid response format - missing required fields');
                }

                const destinationId = this.selectedConsignee.location_id;
                const attachmentData = {
                    id: result.id,
                    name: file.name,
                    attachment_url: result.attachment_url,
                };

                // 5. Update attachments map (reactively)
                if (!this.attachments[destinationId]) {
                    this.$set(this.attachments, destinationId, [attachmentData]);
                } else {
                    // Create new array to ensure reactivity
                    this.$set(this.attachments, destinationId, [
                        ...this.attachments[destinationId],
                        attachmentData
                    ]);
                }

                // 6. Update the specific row (reactively)
                const curr_row = this.mergedMaterials[index];
                if (!curr_row.attachment_ids) {
                    this.$set(curr_row, 'attachment_ids', [attachmentData]);
                } else {
                    this.$set(curr_row, 'attachment_ids', [
                        ...curr_row.attachment_ids,
                        attachmentData
                    ]);
                }

                this.$set(row, 'isUpdated', true);
            } catch (err) {
                const apiError = err && err.data && (err.data.messages || (err.data.errors && err.data.errors[0] && err.data.errors[0].messages));
                this.$error({ message: apiError || this.$getErr('UPLOAD_FAILS'), description: apiError });
            }
        },

        async removeAttachments(file, row, index) {
            try {
                var payload = {
                    attachment_id: file.id,
                };

                const result = await this.$store.dispatch('order/removeAttachments', payload);
                this.markRowUpdated(row);
                if (result === 'OK') {
                    var destinationKey = this.selectedConsignee.location_id;
                    var existingAttachments = (this.attachments[destinationKey] || []);

                    var updatedAttachments = existingAttachments.filter(function (att) {
                        return att.id !== file.id;
                    });

                    if (updatedAttachments.length) {
                        this.$set(this.attachments, destinationKey, updatedAttachments);
                    } else {
                        this.$delete(this.attachments, destinationKey);
                    }
                    this.$set(row, 'isUpdated', true);
                    var curr_row = this.mergedMaterials[index];
                    if (curr_row && Array.isArray(curr_row.attachment_ids)) {
                        this.$set(curr_row, 'attachment_ids',
                            curr_row.attachment_ids.filter(function (att) {
                                if (typeof att === 'string') {
                                    return att !== file.id;
                                } else if (att && typeof att === 'object') {
                                    return att.id !== file.id;
                                }
                                return true;
                            })
                        );
                    }

                } else {
                    throw new Error(result.error || 'Failed to remove attachment');
                }
            } catch (err) {
                this.$error({ message: this.$getErr('DELETE_FILE_FAILS') });
            }
        },
        isLocalRow(index) {
            return index >= (this.modifiedExcessMaterials.length || 0);
        },
    }
};
</script>